import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { t, localePrefix } from "@/helper/helper";
import { baseURL } from "@/services/api";
import { GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import styled from "styled-components";

interface IBuyingTerms {
  data: {
    text: string;
  };
}

interface Props {
  locale: string;
  data: IBuyingTerms;
}

export default function BuyingTerms({ locale, data }: Props) {
  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("buyingTerms", locale),
        item: `${SITE_URL}${localePrefix(locale)}/buying-terms`,
      },
    ],
  };

  // Create WebPage schema
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: `${SITE_URL}${localePrefix(locale)}/buying-terms`,
    name: `${t("buyingTerms", locale)} - Promusic.az`,
    description: `Promusic.az üzərindən edilən alış-veriş üçün qaydalar, qaytarılma və dəyişdirmə şərtləri, çatdırılma və müştəri hüquqları haqqında məlumat`,
    inLanguage: locale,
    isPartOf: {
      "@type": "WebSite",
      url: `${SITE_URL}${localePrefix(locale)}`,
    },
  };

  return (
    <Wrapper className="terms">
      <Head>
        <title>
          {SITE_NAME} {t("buyingTerms", locale)}
        </title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
        />
      </Head>
      <div className="breadcrumb">
        <Link href={`${localePrefix(locale) || "/"}`}>Ana səhifə</Link>
        <span className="breadcrumb-seperator" />
        <Link href={`${localePrefix(locale)}/buying-terms`}>{t("buyingTerms", locale)}</Link>
      </div>
      <div className="terms-content">
        <h1 className="title">{t("buyingTerms", locale)}</h1>
        <div dangerouslySetInnerHTML={{ __html: data.data.text }} />
      </div>
    </Wrapper>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale } = ctx;
    const loanTermsData = await fetch(baseURL + "/alis-sertleri", {
      headers: {
        "x-localization": locale ? locale : "az",
      },
    });

    return {
      props: {
        locale,
        data: await loanTermsData.json(),
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const Wrapper = styled.div`
  min-height: calc(100vh - 400px);

  &.terms {
    width: 1289px;
    margin: 0 auto;
    padding-bottom: 7rem;
    padding-top: 3.2rem;

    .terms-content {
      margin-top: 36px;
      width: 812px;
      margin: auto;

      h1.title {
        font-style: normal;
        font-weight: bold;
        font-size: 3.6rem;
        line-height: 150%;
        color: #040941;
        margin-bottom: 2.4rem;
        text-align: center;
        margin-top: 3.2rem;
      }

      h2 {
        font-style: normal;
        font-weight: bold;
        font-size: 1.7rem;
        line-height: 150%;
        color: #040941;
        margin-bottom: 2.4rem;
        text-align: left;
        margin-top: 3.2rem;
      }

      p {
        font-style: normal;
        font-weight: 500;
        font-size: 2rem;
        line-height: 30px;
        color: #989eaf;
        margin-bottom: 1.6rem;
      }
    }

    @media (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;

      .terms-content {
        width: 100%;

        p {
          font-size: 1.6rem;
        }
      }
    }
  }
`;
